import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ScrollText, Feather, History, BarChart3, Upload, Check, Lock } from 'lucide-react';
import { Book, Title, Rule, IconSlot, Bullets, PageButton, px, INK, INK_SOFT, SEAL, PAGE_INK, inked, type BookEntry, type Go } from './SpellBook';
import { EquityCurve, OutcomeHistogram, ReachCurve, Streaks, type ChartTrade } from '../ledger/Charts';
import { TITLE_FONT, PX_FONT } from './theme';
import { useAppState } from '../../hooks/useAppState';
import { tradesApi, progressApi, strategiesApi, statFieldsApi, type StatField, type StatFieldDef, type StrategyReadiness, type TradeStats, type ReadinessImport, type Trade, type StrategyDef, type TradeMode } from '../../lib/api';
import { csvFromUpload } from '../../lib/unzipCsv';
import { LedgerLeft, LedgerPreview, HistoryList, TradeDetail, blankLedger, sheetR, exitOf, legsOf, type LedgerState } from './LedgerPages';
import { RoutineBuilder } from '../ledger/Routine';

/*
 * The Readiness Progression as a book, in the same four parts as the classic view:
 * the progression board as a quest log, the trade ledger with its history, stats and charts (the classic
 * charts re-inked for parchment), and the Notion import.
 * Numbers come from the trader's own logged trades.
 */

const DAYS = 30, TRADES = 30;
const fmtR = (r: number) => `${r >= 0 ? '+' : ''}${r.toFixed(2)}R`;
const PHASE = { replay: { letter: 'R', tone: '#3E6FA8', name: 'Replay' }, paper: { letter: 'P', tone: '#C98A2E', name: 'Paper' }, next: { letter: 'N', tone: '#3E7A4E', name: 'Next steps' } };

const NONE: StrategyReadiness = { backtestDays: 0, backtestTrades: 0, backtestExpectancy: 0, paperTrades: 0, paperExpectancy: 0, paperUnlocked: false, ready: false, fastTrack: null };

/** Pixel progress bar in the page's ink, notched every tenth */
function Bar({ value, max }: { value: number; max: number }) {
  return (
    <div style={{ alignSelf: 'stretch', margin: `${px(2)}px ${px(2)}px 0`, height: px(5), position: 'relative', background: '#E3CD98', boxShadow: `0 0 0 ${px(1)}px ${INK}`, flex: 'none' }}>
      <div style={{ position: 'absolute', inset: 0, width: `${Math.min(1, value / max) * 100}%`, background: SEAL, transition: 'width 0.6s steps(10)' }} />
      <div style={{ position: 'absolute', inset: 0, background: `repeating-linear-gradient(90deg, transparent 0 calc(10% - ${px(1)}px), ${INK}55 calc(10% - ${px(1)}px) 10%)` }} />
    </div>
  );
}

const Para = ({ children }: { children: ReactNode }) => (
  <p style={{ margin: `${px(2)}px ${px(2)}px 0`, fontFamily: PX_FONT, fontSize: 11, lineHeight: 1.6, color: INK, flex: 'none', alignSelf: 'stretch' }}>{children}</p>
);

const Stat = ({ label, value }: { label: string; value: string }) => (
  <div style={{ textAlign: 'center' }}>
    <div style={{ fontFamily: TITLE_FONT, fontSize: 18, color: INK }}>{value}</div>
    <div style={{ fontFamily: PX_FONT, fontSize: 8, color: INK_SOFT, letterSpacing: '0.1em', textTransform: 'uppercase' }}>{label}</div>
  </div>
);

type Status = 'done' | 'open' | 'locked';
interface QuestDef {
  key: string; phase: keyof typeof PHASE; title: string; sub: string; status: Status; text: string;
  progress?: { n: number; of: number; unit: string };
  /** Shown at the foot of the quest's page; a function gets `go` to turn to another bookmark */
  action?: ReactNode | ((go: Go) => ReactNode);
}

const draftField = { minWidth: 0, boxSizing: 'border-box', background: 'none', border: 'none', borderBottom: `2px dashed ${INK_SOFT}`, padding: '2px 4px', fontFamily: PX_FONT, fontSize: 11, color: INK, outline: 'none' } as const;

const chipBtn = (on: boolean) => ({
  flex: 1, padding: '5px 0', border: 'none', cursor: 'pointer', fontFamily: PX_FONT, fontSize: 10,
  color: on ? '#FBEBC8' : INK, background: on ? SEAL : 'rgba(255,248,226,0.55)', boxShadow: `0 0 0 2px ${INK}`,
}) as const;

export function ReadinessBook({ replay, closed }: { replay?: number; closed?: boolean }) {
  const { user } = useAppState();
  const username = user?.discordUsername || 'trader';
  const [ready, setReady] = useState<Record<string, StrategyReadiness>>({});
  const [done, setDone] = useState<Set<string>>(new Set());
  const [values, setValues] = useState<Record<string, string>>({});
  const [stats, setStats] = useState<TradeStats | null>(null);
  const [trades, setTrades] = useState<Trade[]>([]);
  const [imported, setImported] = useState<ReadinessImport | string | null>(null);
  const [quest, setQuest] = useState<string | null>(null);
  const [building, setBuilding] = useState(false);
  const [strategies, setStrategies] = useState<StrategyDef[]>([]);
  const [strat, setStrat] = useState<string | null>(null);
  const [pickTrade, setPickTrade] = useState<number | null>(null);
  const [fields, setFields] = useState<StatField[]>([]);
  const [catalog, setCatalog] = useState<StatFieldDef[]>([]);
  const [draft, setDraft] = useState<{ name: string; code: string; err: string | null } | null>(null); // new strategy being written

  const [sheet, setSheet] = useState<LedgerState>(() => blankLedger('backtest'));
  const set = (p: Partial<LedgerState>) => setSheet(s => ({ ...s, ...p }));
  const file = useRef<HTMLInputElement>(null);

  const loadFields = () => statFieldsApi.list(username).then(r => { setFields(r.fields); setCatalog(r.catalog); }).catch(() => undefined);
  const refresh = () => {
    tradesApi.readiness(username).then(r => setReady(r.strategies)).catch(() => undefined);
    tradesApi.stats(username).then(r => setStats(r.stats)).catch(() => undefined);
    tradesApi.list(username, { limit: 100 }).then(setTrades).catch(() => undefined);
  };
  useEffect(() => {
    refresh();
    progressApi.list(username).then(r => { setDone(new Set(r.done)); setValues(r.values); }).catch(() => undefined);
    strategiesApi.list(username).then(setStrategies).catch(() => undefined);
    loadFields();
  }, [username]);

  const mark = (key: string, on: boolean, value?: string) => {
    setDone(s => { const n = new Set(s); if (on) n.add(key); else n.delete(key); return n; });
    if (value !== undefined) setValues(v => ({ ...v, [key]: value }));
    progressApi.set(username, key, on, value).catch(() => undefined);
  };

  // The chosen strategy leads the book; until one is chosen, the one furthest along
  const furthest = Object.entries(ready).sort(([, a], [, b]) => (b.backtestDays + b.paperTrades * 2) - (a.backtestDays + a.paperTrades * 2))[0];
  const [code, r]: [string, StrategyReadiness] = strat ? [strat, ready[strat] ?? NONE] : furthest ?? [strategies[0]?.code ?? '—', NONE];
  const createStrategy = async () => {
    if (!draft) return;
    try {
      const made = await strategiesApi.create(username, { name: draft.name, code: draft.code });
      setStrategies(list => [...list, made]);
      setStrat(made.code);
      setDraft(null);
    } catch (err) { setDraft(d => d && { ...d, err: err instanceof Error ? err.message : 'Could not create strategy' }); }
  };
  // Turn to the ledger with the right trial selected
  const logBtn = (mode: TradeMode) => (go: Go) => <PageButton onClick={() => { set({ mode }); go('log'); }}>Log a trade</PageButton>;
  const picker = (
    <div className="sb-scroll" style={{ alignSelf: 'stretch', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: px(4), maxHeight: px(46), overflowY: 'auto', padding: 3 }}>
      {strategies.map(sd => {
        const on = sd.code === code, rd = ready[sd.code];
        return (
          <button key={sd.code} type="button" onClick={() => setStrat(sd.code)} style={{
            padding: '5px 6px', border: 'none', cursor: 'pointer', textAlign: 'left', boxShadow: `0 0 0 2px ${on ? SEAL : 'rgba(91,58,42,0.45)'}`,
            background: on ? 'rgba(142,47,31,0.1)' : 'none', fontFamily: PX_FONT, color: INK,
          }}>
            <span style={{ display: 'block', fontSize: 11, color: on ? SEAL : INK }}>{sd.code}</span>
            <span style={{ display: 'block', fontSize: 8, color: INK_SOFT, textTransform: 'uppercase', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {rd ? (rd.ready ? 'trials passed' : rd.paperUnlocked ? `paper ${rd.paperTrades}/30` : `replay ${rd.backtestDays}/30`) : sd.name}
            </span>
          </button>
        );
      })}
      {/* Write your own: a blank tile that opens into name + code */}
      {draft === null ? (
        <button type="button" onClick={() => setDraft({ name: '', code: '', err: null })} style={{
          padding: '5px 6px', border: 'none', cursor: 'pointer', boxShadow: '0 0 0 2px rgba(91,58,42,0.45)', background: 'none',
          fontFamily: PX_FONT, fontSize: 11, color: INK_SOFT, borderStyle: 'dashed',
        }}>+ Your own</button>
      ) : (
        <form onSubmit={e => { e.preventDefault(); createStrategy(); }} style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: '1fr 70px auto', gap: 6, alignItems: 'end', padding: '4px 2px', boxShadow: `0 0 0 2px ${SEAL}` }}>
          <input autoFocus required maxLength={40} placeholder="Name" value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} style={draftField} />
          <input required maxLength={8} placeholder="CODE" value={draft.code} onChange={e => setDraft({ ...draft, code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })} style={draftField} />
          <button type="submit" style={{ border: 'none', cursor: 'pointer', padding: '4px 8px', fontFamily: PX_FONT, fontSize: 9, color: '#FBEBC8', background: SEAL }}>Add</button>
          {draft.err && <span style={{ gridColumn: '1 / -1', fontFamily: PX_FONT, fontSize: 8, color: SEAL }}>{draft.err}</span>}
        </form>
      )}
    </div>
  );
  const st = (doneIf: boolean, openIf = true): Status => (doneIf ? 'done' : openIf ? 'open' : 'locked');

  const quests: QuestDef[] = [
    { key: 'strategy', phase: 'replay', title: 'Choose your strategy', sub: code === '—' ? 'none yet' : code, status: 'done',
      text: 'Pick the strategy you will prove out. Each strategy runs its own replay and paper trials.', action: picker },
    { key: 'commence_replay', phase: 'replay', title: 'Commence bar replay', sub: r.backtestTrades ? `${r.backtestTrades} trades in` : 'enter your first trade', status: st(r.backtestTrades > 0),
      text: 'Open bar replay and take your first trade by the rules. Log it in the ledger.', action: logBtn('backtest') },
    { key: 'finish_replay', phase: 'replay', title: 'Finish bar replay', sub: `${Math.min(r.backtestDays, DAYS)}/${DAYS} days · ${fmtR(r.backtestExpectancy)}`, status: st(r.paperUnlocked),
      text: 'Thirty trading days of replay, finishing with positive expectancy. This opens paper trading.',
      progress: { n: r.backtestDays, of: DAYS, unit: 'trading days' }, action: r.backtestDays < DAYS ? logBtn('backtest') : undefined },
    { key: 'sim_account', phase: 'paper', title: 'Set up a sim account', sub: done.has('sim_account') ? 'ready' : 'demo or paper', status: st(done.has('sim_account'), r.paperUnlocked),
      text: 'Open a simulated or demo account on your platform so paper trades run on live data.',
      // One checkbox to tick (and untick) rather than separate done / undo buttons
      action: r.paperUnlocked && (
        <button type="button" onClick={() => mark('sim_account', !done.has('sim_account'))} style={{
          marginTop: 'auto', display: 'flex', alignItems: 'center', gap: 10, border: 'none', background: 'none', cursor: 'pointer',
          fontFamily: PX_FONT, fontSize: 11, color: INK, padding: 4,
        }}>
          <span style={{ width: 20, height: 20, display: 'grid', placeItems: 'center', boxShadow: `0 0 0 2px ${INK}`, background: done.has('sim_account') ? SEAL : '#F2E2B6', color: '#FBEBC8' }}>
            {done.has('sim_account') && <Check size={14} strokeWidth={3} />}
          </span>
          My sim account is set up
        </button>
      ) },
    { key: 'commence_paper', phase: 'paper', title: 'Commence paper trading', sub: r.paperTrades ? `${r.paperTrades} trades in` : 'first paper trade', status: st(r.paperTrades > 0, r.paperUnlocked),
      text: 'Take your first paper trade on the sim account and log it as Paper.', action: r.paperUnlocked ? logBtn('paper') : undefined },
    { key: 'finish_paper', phase: 'paper', title: 'Finish paper trading', sub: `${Math.min(r.paperTrades, TRADES)}/${TRADES} trades · ${fmtR(r.paperExpectancy)}`, status: st(r.ready, r.paperUnlocked),
      text: 'Thirty paper trades, finishing with positive expectancy. This opens the next steps.',
      progress: { n: r.paperTrades, of: TRADES, unit: 'paper trades' }, action: r.paperUnlocked && r.paperTrades < TRADES ? logBtn('paper') : undefined },
    { key: 'open_account', phase: 'next', title: 'Open a trading account', sub: values.open_account ?? 'live or prop', status: st(done.has('open_account'), r.ready),
      text: 'Choose where you go live: your own account, or a prop firm evaluation.',
      action: r.ready && (
        <div style={{ display: 'flex', gap: 8, alignSelf: 'stretch', marginTop: 'auto', padding: '0 4px' }}>
          {[['live', 'Live'], ['prop', 'Prop']].map(([v, l]) => <button key={v} type="button" onClick={() => mark('open_account', true, v)} style={chipBtn(values.open_account === v)}>{l}</button>)}
        </div>
      ) },
    { key: 'trading_routine', phase: 'next', title: 'Design your routine', sub: done.has('trading_routine') ? 'written' : 'your daily plan', status: st(done.has('trading_routine'), r.ready),
      text: 'Write the routine you will follow each session: preparation, limits, and when you stop.',
      action: r.ready && <PageButton onClick={() => setBuilding(true)}>Build</PageButton> },
    { key: 'prescription', phase: 'next', title: 'Generate your prescription', sub: 'opens the Engine', status: st(false, r.ready),
      text: 'Your risk numbers, sized from your own trades and your firm’s rules.',
      action: r.ready && <PageButton href="/hub?open=rx">Engine</PageButton> },
  ];
  // Open on the first unfinished quest
  const current = quest ?? (quests.find(q => q.status === 'open')?.key ?? quests[0].key);
  const q = quests.find(x => x.key === current)!;

  const inscribe = async (go: Go) => {
    const res = sheetR(sheet);
    if (res === null) return;
    set({ busy: true, note: null });
    try {
      const t = await tradesApi.create(username, {
        mode: sheet.mode, strategy: (code === '—' ? (user?.currentStrategy ?? 'VCL') : code) as never, pair: sheet.pair, traded_on: sheet.date, session: sheet.session, timeframe: sheet.timeframe || null,
        entry_price: Number(sheet.entry), stop_price: Number(sheet.stop), max_run_price: Number(sheet.run), exit_price: Number(exitOf(sheet)),
        quantity: sheet.qty ? Number(sheet.qty) : null, max_heat_price: sheet.heat ? Number(sheet.heat) : null, partials: legsOf(sheet),
        context: sheet.context, followed_rules: sheet.followed, notes: sheet.notes.trim() || undefined,
      });
      if (sheet.image) await tradesApi.uploadImage(username, t.id, sheet.image).catch(() => undefined);
      refresh();
      setSheet({ ...blankLedger(sheet.mode), pair: sheet.pair, session: sheet.session, qty: sheet.qty, note: `Inscribed ${fmtR(res)}` });
      setPickTrade(t.id);
      go('log', true);
    } catch (err) { set({ busy: false, note: err instanceof Error ? err.message : 'Could not save the trade' }); }
  };

  // Oldest first, so curves and streaks read left to right in time
  const series: ChartTrade[] = trades.slice().reverse().map(t => ({ r: Number(t.r_multiple), mr: t.max_run_r === null ? null : Math.max(0, Number(t.max_run_r)) }));

  const runImport = async (f: File) => {
    try { setImported(await tradesApi.importReadiness(username, await csvFromUpload(f))); refresh(); }
    catch (err) { setImported(err instanceof Error ? err.message : 'Import failed'); }
  };

  const entries: BookEntry[] = [
    {
      key: 'progression', name: 'Progression', icon: <ScrollText size={30} />,
      // Quest log: every step of the path, ticked from the trader's own data
      left: (
        <>
          <Title>Quests</Title>
          <div className="sb-scroll" style={{ alignSelf: 'stretch', flex: 1, minHeight: 0, overflowY: 'auto', marginTop: 8, paddingRight: 4 }}>
            {quests.map(x => {
              const on = x.key === current, ph = PHASE[x.phase];
              return (
                <button key={x.key} type="button" onClick={() => setQuest(x.key)} style={{
                  position: 'relative', width: '100%', display: 'grid', gridTemplateColumns: '26px 1fr 18px', alignItems: 'center', gap: 8, marginBottom: 6,
                  padding: '4px 6px', border: 'none', cursor: 'pointer', textAlign: 'left',
                  background: on ? 'rgba(142,47,31,0.08)' : 'none', borderBottom: `2px dotted rgba(91,58,42,0.35)`,
                  opacity: x.status === 'locked' ? 0.55 : 1,
                }}>
                  {on && <span style={{ position: 'absolute', left: -12, top: '50%', marginTop: -5, borderLeft: `7px solid ${SEAL}`, borderTop: '5px solid transparent', borderBottom: '5px solid transparent' }} />}
                  <span style={{ width: 24, height: 24, display: 'grid', placeItems: 'center', fontFamily: TITLE_FONT, fontSize: 15, color: '#FBEBC8', background: ph.tone, boxShadow: `0 0 0 2px ${INK}` }}>{ph.letter}</span>
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', fontFamily: PX_FONT, fontSize: 10, color: INK, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{x.title}</span>
                    <span style={{ display: 'block', fontFamily: PX_FONT, fontSize: 8, color: INK_SOFT, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{x.sub}</span>
                  </span>
                  <span style={{ width: 16, height: 16, display: 'grid', placeItems: 'center', boxShadow: `0 0 0 2px ${INK}`, background: x.status === 'done' ? SEAL : '#F2E2B6', color: '#FBEBC8' }}>
                    {x.status === 'done' ? <Check size={11} strokeWidth={3} /> : x.status === 'locked' ? <Lock size={9} color={INK_SOFT} /> : null}
                  </span>
                </button>
              );
            })}
          </div>
        </>
      ),
      right: go => (
        <>
          {/* The shrine builds up quest by quest: a staked plot at the start, the finished shrine at the end */}
          <span key={q.key} style={{ position: 'relative', display: 'grid', placeItems: 'center', flex: 'none' }}>
            <img src={`/assets/pixel/build-${quests.indexOf(q) + 1}.webp`} alt="" style={{ ...inked, height: px(38), maxWidth: '100%', objectFit: 'contain' }} />
            {/* The finished shrine is crowned: a shaft of light and a few glints, drawn in gold leaf */}
            {quests.indexOf(q) === quests.length - 1 && (
              <>
                <span style={{ position: 'absolute', left: '50%', top: -px(4), width: px(5), height: px(22), marginLeft: -px(2.5), background: 'linear-gradient(180deg, rgba(201,138,46,0), rgba(201,138,46,0.45) 60%, rgba(201,138,46,0))', animation: 'sb-beam 2.4s ease-in-out infinite' }} />
                {[[18, 10], [78, 18], [30, 52], [70, 60], [50, 4], [88, 40]].map(([x, y], k) => (
                  <span key={k} style={{ position: 'absolute', left: `${x}%`, top: `${y}%`, width: 5, height: 5, background: '#C98A2E', boxShadow: '0 0 0 1px rgba(91,58,42,0.4)', animation: `sb-glint 1.8s steps(3) ${k * 0.3}s infinite` }} />
                ))}
              </>
            )}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, alignSelf: 'stretch', marginTop: 8 }}>
            <span style={{ width: 22, height: 22, display: 'grid', placeItems: 'center', fontFamily: TITLE_FONT, fontSize: 14, color: '#FBEBC8', background: PHASE[q.phase].tone, boxShadow: `0 0 0 2px ${INK}`, flex: 'none' }}>{PHASE[q.phase].letter}</span>
            <span style={{ fontFamily: TITLE_FONT, fontSize: 20, color: INK, lineHeight: 1 }}>{q.title}</span>
          </div>
          <Para>{q.status === 'locked' ? `Sealed until the ${q.phase === 'paper' ? 'replay' : 'paper'} trial is complete. ` : ''}{q.text}</Para>
          {q.progress && <><Bar value={q.progress.n} max={q.progress.of} /><div style={{ fontFamily: PX_FONT, fontSize: 9, color: INK_SOFT, marginTop: 4 }}>{Math.min(q.progress.n, q.progress.of)} / {q.progress.of} {q.progress.unit}</div></>}
          {(typeof q.action === 'function' ? q.action(go) : q.action) || null}
        </>
      ),
    },
    {
      key: 'log', name: 'Log a trade', icon: <Feather size={30} />,
      left: <LedgerLeft s={sheet} set={set} paperOpen={r.paperUnlocked} username={username} fields={fields} catalog={catalog}
        onAddStat={key => { statFieldsApi.addCatalog(username, key).then(loadFields).catch(() => undefined); }} />,
      right: go => <LedgerPreview s={sheet} recent={trades} onLog={() => inscribe(go)} onSeeHistory={() => go('history')} />,
    },
    {
      key: 'history', name: 'History', icon: <History size={30} />,
      left: (
        <>
          <Title>History</Title>
          <HistoryList trades={trades} pick={pickTrade ?? trades[0]?.id ?? null} onPick={setPickTrade} />
        </>
      ),
      right: <TradeDetail username={username} t={trades.find(t => t.id === (pickTrade ?? trades[0]?.id))} onRemoved={() => { setPickTrade(null); refresh(); }} />,
    },
    {
      // Everything on one spread: numbers, streaks and outcomes on the left; equity and reach on the right
      key: 'stats', name: 'Stats', icon: <BarChart3 size={30} />,
      left: (
        <>
          <Title>Stats</Title>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4, marginTop: px(3), alignSelf: 'stretch' }}>
            <Stat label="Trades" value={String(stats?.trades ?? 0)} />
            <Stat label="Win" value={`${Math.round((stats?.winRate ?? 0) * 100)}%`} />
            <Stat label="Exp." value={fmtR(stats?.expectancyR ?? 0)} />
            <Stat label="Total" value={fmtR(stats?.totalR ?? 0)} />
          </div>
          <div className="page-ink" style={{ ...PAGE_INK, alignSelf: 'stretch', marginTop: px(3), overflow: 'hidden', flex: 'none' }}>{series.length > 0 && <Streaks trades={series} last={16} />}</div>
          <div className="page-ink" style={{ ...PAGE_INK, alignSelf: 'stretch', marginTop: px(2), overflow: 'hidden' }}>{series.length > 0 && <OutcomeHistogram trades={series} height={150} w={400} />}</div>
        </>
      ),
      right: (
        <>
          <Title>Curves</Title>
          <div className="page-ink" style={{ ...PAGE_INK, alignSelf: 'stretch', marginTop: px(3), overflow: 'hidden', flex: 'none' }}>{series.length > 0 && <EquityCurve trades={series} height={160} w={400} />}</div>
          <div className="page-ink" style={{ ...PAGE_INK, alignSelf: 'stretch', marginTop: px(3), overflow: 'hidden' }}>
            {series.length ? <ReachCurve trades={series} height={165} w={420} /> : <Para>Log trades with prices to draw your reach curve.</Para>}
          </div>
        </>
      ),
    },
    {
      key: 'import', name: 'Import', icon: <Upload size={30} />,
      left: (
        <>
          <Title>Import</Title>
          <Rule />
          <span style={{ marginTop: px(4) }}><IconSlot size={34}><Upload size={30} /></IconSlot></span>
          <Para>Bring in your Notion Readiness tracker: export the Master Trade Log and drop the ZIP or CSV here.</Para>
        </>
      ),
      right: (
        <>
          <Title>Archive</Title>
          <Rule />
          {imported === null ? <Bullets items={['Replay and paper trades', 'Duplicates skipped', 'Stats switched on']} />
            : typeof imported === 'string' ? <Para>{imported}</Para>
            : <Bullets items={[`${imported.imported} trades brought in`, `${imported.duplicates} already here`, `${imported.skipped.length} skipped`]} />}
          <input ref={file} type="file" accept=".csv,.zip" hidden onChange={e => { const f = e.target.files?.[0]; if (f) runImport(f); e.target.value = ''; }} />
          <PageButton onClick={() => file.current?.click()}>Choose file</PageButton>
        </>
      ),
    },
  ];
  return (
    <>
      <Book title="Readiness" entries={entries} replay={replay} closed={closed} />
      {building && <RoutineBuilder username={username} onClose={() => setBuilding(false)} onSaved={() => { setBuilding(false); mark('trading_routine', true); }} />}
    </>
  );
}
